import { useEffect, useState } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { Badge, Box, Button, Flex, Spinner, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FaFileInvoice, FaPlus } from "react-icons/fa";
import { FiscalInvoiceListItem, getFiscalInvoices } from "../../../services/fiscalService";
import { crm } from "../../../theme";
import SectionCard from "./SectionCard";
import { brl } from "./duimp";
import { TableCard, tdProps, thProps } from "./tableStyles";
import ImportEntryModal from "./ImportEntryModal";
import FiscalInvoiceModal from "./FiscalInvoiceModal";
import { operationLabel, statusInfo } from "./fiscalLabels";
import ShipmentModal from "./ShipmentModal";
import ChildrenModal from "./ChildrenModal";
import SaleModal from "../../commercial/sales/SaleModal";


/** Notas fiscais do processo: lista + gerar NF de entrada a partir da DUIMP. */
const FiscalInvoicesPanel = ({ processId }: { processId: string }) => {
  const toast = useToast();
  const [list, setList] = useState<FiscalInvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [shipmentFor, setShipmentFor] = useState<string | null>(null);
  const [childrenFor, setChildrenFor] = useState<string | null>(null);
  const [sale, setSale] = useState<{ entryId?: string; invoiceId?: string } | null>(null);
  const auth = useAuthUser<any>();
  // Mesmo grupo que o servidor deixa emitir (FiscalHandler.CanManage): admin, Administração, Operacional, Financeiro
  const canManage = !!auth?.admin || [2, 3, 5].includes(auth?.position);

  const load = async () => {
    setLoading(true);
    try { setList(await getFiscalInvoices({ processId })); }
    catch { toast({ status: "error", title: "Não foi possível carregar as notas fiscais" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [processId]);

  return (
    <SectionCard
      icon={<FaFileInvoice />}
      title="Notas fiscais"
      subtitle="NF-e do processo (Focus NFe)"
      actions={canManage ? (
        <Button size="sm" leftIcon={<FaPlus />} bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={() => setGenerateOpen(true)}>
          Gerar NF de entrada
        </Button>
      ) : undefined}
    >
      {loading ? (
        <Flex justify="center" py="16px"><Spinner /></Flex>
      ) : list.length === 0 ? (
        <Text fontSize="13px" color={crm.textMuted}>Nenhuma nota fiscal neste processo.</Text>
      ) : (
        <TableCard>
          <Thead>
            <Tr>
              <Th {...thProps}>Nota</Th>
              <Th {...thProps}>Operação</Th>
              <Th {...thProps}>Emitente</Th>
              <Th {...thProps}>Situação</Th>
              <Th {...thProps} isNumeric>Total</Th>
              <Th {...thProps}>Criada em</Th>
            </Tr>
          </Thead>
          <Tbody>
            {list.map((nf) => (
              <Tr key={nf.id} cursor="pointer" _hover={{ bg: crm.subtle }} onClick={() => setOpenId(nf.id)}>
                <Td {...tdProps} fontWeight="600">{nf.number ? `Nº ${nf.number}` : "—"}</Td>
                <Td {...tdProps}>
                  {operationLabel[nf.operation] ?? nf.operation}
                  {nf.container && <Text fontSize="11px" color={crm.textMuted}>{nf.container}</Text>}
                </Td>
                <Td {...tdProps}>
                  <Box>{nf.issuerName}</Box>
                  {(nf.environment ?? nf.issuerEnvironment) === "homologacao" && <Badge colorScheme="purple" fontSize="10px">Homologação</Badge>}
                </Td>
                <Td {...tdProps}><Badge colorScheme={statusInfo[nf.status]?.color}>{statusInfo[nf.status]?.label ?? nf.status}</Badge></Td>
                <Td {...tdProps} isNumeric>{brl(nf.total)}</Td>
                <Td {...tdProps} color={crm.textMuted}>{DateTime.fromISO(nf.createdDate).toFormat("dd/LL/yyyy HH:mm")}</Td>
              </Tr>
            ))}
          </Tbody>
        </TableCard>
      )}

      {generateOpen && (
        <ImportEntryModal
          processId={processId}
          onClose={() => setGenerateOpen(false)}
          onCreated={(id) => { setGenerateOpen(false); load(); setOpenId(id); }}
          onOpenInvoice={(id) => { setGenerateOpen(false); setOpenId(id); }}
        />
      )}
      {openId && (
        <FiscalInvoiceModal
          invoiceId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
          onShipment={canManage ? (id) => { setOpenId(null); setShipmentFor(id); } : undefined}
          onChildren={canManage ? (id) => { setOpenId(null); setChildrenFor(id); } : undefined}
          onSale={canManage ? (id) => { setOpenId(null); setSale({ entryId: id }); } : undefined}
          onEditSale={canManage ? (id) => { setOpenId(null); setSale({ invoiceId: id }); } : undefined}
        />
      )}
      {shipmentFor && (
        <ShipmentModal
          entryId={shipmentFor}
          onClose={() => setShipmentFor(null)}
          onCreated={(id) => { setShipmentFor(null); load(); setOpenId(id); }}
          onOpenInvoice={(id) => { setShipmentFor(null); setOpenId(id); }}
        />
      )}
      {childrenFor && (
        <ChildrenModal motherId={childrenFor} onClose={() => setChildrenFor(null)} onCreated={() => { setChildrenFor(null); load(); }} />
      )}
      {sale && (
        <SaleModal
          entryId={sale.entryId}
          invoiceId={sale.invoiceId}
          onClose={() => setSale(null)}
          onSaved={(id) => { setSale(null); load(); setOpenId(id); }}
        />
      )}
    </SectionCard>
  );
};

export default FiscalInvoicesPanel;
